export const en = {
  nav: {
    home: 'Home',
    about: 'About',
    hybrid: 'Hybrid Workforce',
    businesses: 'Businesses',
    solutions: 'Solutions',
    insights: 'Insights',
    careers: 'Careers',
    contact: 'Contact'
  },
  home: {
    hero: {
      title: 'We Design the Future of Work.',
      human: 'Human Intelligence',
      ai: 'AI Optimization',
      robotics: 'Robotic Execution'
    },
    philosophy: {
      headline: 'Hybrid Workforce is not automation. It\'s architecture.',
      body: 'We design the future of work by integrating Humans, AI, and Robotics into a single workforce architecture. This is not about replacing people—it\'s about creating a better work environment where each element operates at its highest potential.'
    },
    businesses: {
      manpower: {
        label: 'Human Core',
        title: 'Manpower',
        description: 'As a global leader in workforce solutions with over 70 years of history, we connect top talent to roles requiring strategic thinking and accountability.'
      },
      ai: {
        label: 'Intelligence Layer',
        title: 'AI Layer',
        description: 'An intelligent system that connects human and robotic workforces through prediction, optimization, and decision support.'
      },
      robotics: {
        label: 'Robotic Core',
        title: 'BC Robotics',
        description: 'Advanced robotic solutions for repetitive and precision tasks, maximizing operational efficiency.'
      }
    },
    cta: {
      headline: 'Ready to Redesign Your Workforce?',
      subtext: 'We provide customized hybrid workforce solutions for enterprise clients.',
      button: 'Start Conversation'
    }
  },
  about: {
    opening: {
      headline: 'We Believe Work Should Be Redesigned, Not Replaced',
      team: 'Led by workforce architects, not technologists'
    },
    vision: {
      why: {
        title: 'Why We Exist',
        content: 'Traditional labor models have reached their limits. We create more efficient and human-centered work environments by combining human judgment, AI intelligence, and robotic precision. BrainCommerce doesn\'t just provide technology—we redefine how work is done.'
      },
      broken: {
        title: 'The Broken Labor Model',
        content: 'The existing labor market has tried to solve problems with either humans alone or technology alone. But true innovation comes from the harmony of both. We design workforces where each element focuses on what it does best.'
      },
      manifesto: '"We don\'t replace people. We redesign systems so people can do more meaningful work. This is the essence of Hybrid Workforce."'
    }
  },
  hybrid: {
    hero: {
      title: 'Hybrid Workforce',
      definition: 'Hybrid Workforce is an integrated workforce architecture where Humans, AI, and Robotics collaborate, each leveraging their unique strengths.',
      human: 'Human',
      ai: 'AI',
      robotics: 'Robotics'
    },
    roles: {
      human: {
        title: 'Human',
        point1: 'Strategic judgment and decision-making',
        point2: 'Complex problem solving',
        point3: 'Creativity and innovation',
        point4: 'Responsibility and ethical oversight'
      },
      ai: {
        title: 'AI',
        point1: 'Data analysis and prediction',
        point2: 'Process optimization',
        point3: 'Pattern recognition and learning',
        point4: 'Real-time decision support'
      },
      robotics: {
        title: 'Robotics',
        point1: 'Repetitive task execution',
        point2: 'Precise physical operations',
        point3: '24/7 operational capability',
        point4: 'Hazardous environment work'
      },
      learnMore: 'Learn More'
    },
    diagram: {
      title: 'Hybrid Workforce Process',
      task: 'Task',
      human: 'Human',
      humanRole: 'Judgment',
      ai: 'AI',
      aiRole: 'Optimization',
      robotics: 'Robotics',
      roboticsRole: 'Execution',
      result: 'Result'
    }
  },
  businesses: {
    overview: {
      title: 'Our Ecosystem',
      description: 'BrainCommerce is an integrated ecosystem composed of three core elements. Manpower handles the human workforce, BC Robotics manages the robotic workforce, and the AI Layer connects everything.',
      manpower: 'Manpower',
      aiLayer: 'AI Layer',
      robotics: 'BC Robotics'
    },
    manpower: {
      title: 'Manpower: Human Workforce Core',
      description: 'A global workforce solutions leader with over 70 years of history. We connect top talent to roles requiring strategic thinking and accountability.',
      service1: 'Global talent recruitment and placement',
      service2: 'Workforce consulting and strategy',
      service3: 'Talent development and training',
      service4: 'Organizational design and optimization',
      cta: 'Explore Manpower'
    },
    ai: {
      title: 'AI Layer: Intelligence Connection',
      description: 'An intelligent system connecting human and robotic workforces. Maximizes overall workforce efficiency through prediction, optimization, and real-time decision support.',
      service1: 'Workforce analysis and forecasting',
      service2: 'Task allocation optimization',
      service3: 'Performance monitoring and improvement',
      service4: 'Intelligent automation systems',
      cta: 'Explore AI Solutions'
    },
    robotics: {
      title: 'BC Robotics: Robotic Workforce Core',
      description: 'Provides advanced robotic solutions for repetitive and precision tasks. Revolutionizes operational efficiency in logistics, manufacturing, and service industries.',
      service1: 'Autonomous Mobile Robots (AMR)',
      service2: 'Collaborative Robots (Cobot)',
      service3: 'Robotic Process Automation',
      service4: 'Smart logistics systems',
      cta: 'Explore BC Robotics'
    },
    maintenance: {
      title: 'Maintenance & Service: Operational Excellence',
      description: 'Comprehensive maintenance and service solutions for robotics and smart factory equipment. We ensure maximum uptime and optimal performance of your automated systems.',
      service1: 'Preventive maintenance and inspection',
      service2: 'Emergency repair and technical support',
      service3: 'Equipment upgrade and optimization',
      service4: 'Remote monitoring and diagnostics',
      cta: 'Explore Maintenance Services'
    }
  },
  solutions: {
    tabs: {
      hr: 'For HR Leaders',
      ceo: 'For CEOs',
      industry: 'By Industry'
    },
    hr: {
      challenge: 'A New Paradigm in Workforce Design',
      benefit1: 'Task-based workforce design',
      benefit2: 'Talent optimization and placement',
      benefit3: 'Future capability development',
      case1: {
        company: 'Global Manufacturing Company',
        challenge: 'Productivity decline due to skilled labor shortage and high turnover',
        solution: 'Implemented hybrid workforce model: robots for repetitive tasks, humans for quality control',
        result: '40% productivity increase, 25% turnover reduction',
        metric: 'Productivity Increase'
      },
      case2: {
        company: 'Logistics Service Company',
        challenge: 'Need for flexible workforce to handle seasonal demand fluctuations',
        solution: 'AI-based demand forecasting and hybrid workforce for peak season response',
        result: '60% operational cost reduction, faster delivery times',
        metric: 'Cost Reduction'
      },
      case3: {
        company: 'Service Industry Company',
        challenge: 'Need to improve operational efficiency while maintaining customer service quality',
        solution: 'Automated routine tasks, humans focused on customer relationships',
        result: '3x increase in customer satisfaction, improved employee satisfaction',
        metric: 'Customer Satisfaction'
      }
    },
    ceo: {
      challenge: 'Strategy for Sustainable Growth',
      benefit1: 'Scalable workforce model',
      benefit2: 'Operational cost optimization',
      benefit3: 'Competitive advantage',
      case1: {
        company: 'Mid-sized Manufacturing Company',
        challenge: 'Need to expand workforce for global expansion',
        solution: 'Achieved rapid expansion and quality maintenance with hybrid workforce',
        result: '$2.5M annual cost savings',
        metric: 'Annual Savings'
      },
      case2: {
        company: 'E-commerce Company',
        challenge: 'Logistics bottleneck due to rapid growth',
        solution: 'Expanded processing capacity with AI and robot-based logistics system',
        result: '200% processing capacity increase',
        metric: 'Capacity Increase'
      },
      case3: {
        company: 'Healthcare Company',
        challenge: 'Balance between regulatory compliance and efficiency improvement',
        solution: 'Humans for decision-making and oversight, robots for precision tasks',
        result: '85% improvement in regulatory compliance',
        metric: 'Compliance Rate'
      }
    },
    industry: {
      challenge: 'Industry-Specific Solutions',
      benefit1: 'Logistics: Smart warehousing and delivery',
      benefit2: 'Manufacturing: Flexible production lines',
      benefit3: 'Services: Enhanced customer experience',
      case1: {
        company: 'Logistics Center',
        challenge: 'Insufficient processing capacity during peak season',
        solution: 'Built collaboration system between AMRs and human workers',
        result: '50% processing speed improvement',
        metric: 'Speed Improvement'
      },
      case2: {
        company: 'Auto Parts Manufacturing',
        challenge: 'Need to handle high-mix low-volume production',
        solution: 'Flexible production system with collaborative robots and skilled workers',
        result: '99.8% defect rate reduction',
        metric: 'Quality Improvement'
      },
      case3: {
        company: 'Hotel Chain',
        challenge: 'Maintaining service quality amid labor shortage',
        solution: 'Robots for room service and cleaning, humans for guest interaction',
        result: '70% operational cost reduction',
        metric: 'Cost Reduction'
      }
    },
    contactCta: 'Request Consultation'
  },
  insights: {
    featured: {
      category: 'Workforce Design',
      title: 'Why Hybrid Workforce Becomes the Standard in 2025',
      author: 'Dr. Jihoon Kim',
      date: 'January 15, 2025',
      readTime: '8 min read'
    },
    filters: {
      all: 'All',
      workforce: 'Workforce Design',
      case: 'Case Studies',
      research: 'Research'
    },
    articles: {
      article1: {
        category: 'Workforce Design',
        title: 'Human-Robot Collaboration: A New Work Model',
        excerpt: 'Introducing key principles and practical strategies for building an effective hybrid workforce.',
        author: 'Minsu Park',
        date: '2025.01.10'
      },
      article2: {
        category: 'Technology',
        title: 'How AI Revolutionizes Workforce Management',
        excerpt: 'Exploring how predictive analytics and optimization algorithms maximize workforce efficiency.',
        author: 'Seoyeon Lee',
        date: '2025.01.08'
      },
      article3: {
        category: 'Case Study',
        title: 'Manufacturing Digital Transformation Success Story',
        excerpt: 'Sharing how a global manufacturer improved productivity by 40% with hybrid workforce.',
        author: 'Junho Choi',
        date: '2025.01.05'
      },
      article4: {
        category: 'Insights',
        title: 'The Future of Work: Redefining Human Roles',
        excerpt: 'Exploring core competencies and values humans should focus on in the automation era.',
        author: 'Sujin Jung',
        date: '2025.01.03'
      },
      article5: {
        category: 'Logistics',
        title: 'The Future of Smart Logistics: Harmony of AMR and Humans',
        excerpt: 'Examining how autonomous mobile robots are transforming the logistics industry.',
        author: 'Dongwook Kang',
        date: '2024.12.28'
      },
      article6: {
        category: 'Research',
        title: 'Economic Impact of Hybrid Workforce',
        excerpt: 'Analyzing the ROI and long-term value of hybrid workforce through latest research.',
        author: 'Hyejin Yoon',
        date: '2024.12.25'
      }
    }
  },
  careers: {
    hero: {
      title: 'Design the Future of Work With Us',
      subtitle: 'Grow together in a culture focused on responsibility, design, and long-term thinking.',
      cta: 'View Open Positions'
    },
    culture: {
      title: 'Our Values',
      value1: {
        title: 'Long-term Thinking',
        description: 'We build sustainable futures, not chase short-term results.'
      },
      value2: {
        title: 'Responsibility',
        description: 'We understand our decisions impact people\'s work and lives.'
      },
      value3: {
        title: 'Design Excellence',
        description: 'We design everything thoughtfully and pay attention to details.'
      },
      value4: {
        title: 'Collaboration',
        description: 'Diverse perspectives and expertise come together to create the best solutions.'
      },
      value5: {
        title: 'Innovation',
        description: 'We challenge existing methods and explore new possibilities.'
      }
    },
    openings: {
      title: 'Open Positions',
      description: 'Job Description',
      requirements: 'Requirements',
      apply: 'Apply Now'
    },
    positions: {
      pos1: {
        title: 'Workforce Architect',
        department: 'Strategy',
        location: 'Seoul',
        description: 'Design and implement hybrid workforce strategies for enterprises. Find optimal combinations of humans, AI, and robots to achieve client business goals.',
        requirements: '5+ years in workforce management or organizational design, strategic thinking, data analysis capabilities'
      },
      pos2: {
        title: 'AI Engineer',
        department: 'Technology',
        location: 'Seoul',
        description: 'Develop AI systems for workforce optimization. Build predictive models, optimization algorithms, and decision support systems.',
        requirements: '3+ years in ML/AI development, proficient in Python/TensorFlow, understanding of optimization algorithms'
      },
      pos3: {
        title: 'Robotics Engineer',
        department: 'BC Robotics',
        location: 'Seoul',
        description: 'Design and implement industrial robot systems. Optimize various robot solutions including AMR and Cobot for customer environments.',
        requirements: '4+ years in robotics engineering, proficient in ROS, understanding of control systems'
      },
      pos4: {
        title: 'Business Development Manager',
        department: 'Sales',
        location: 'Seoul/Singapore',
        description: 'Propose hybrid workforce solutions to enterprise clients and build partnerships.',
        requirements: '5+ years in B2B sales, enterprise sales experience, excellent communication skills'
      },
      pos5: {
        title: 'UX Designer',
        department: 'Design',
        location: 'Seoul',
        description: 'Design user experience for workforce management platform. Make complex systems intuitive and easy to use.',
        requirements: '3+ years in UX design, B2B SaaS experience preferred, proficient in Figma'
      },
      pos6: {
        title: 'Data Scientist',
        department: 'Analytics',
        location: 'Seoul',
        description: 'Analyze workforce data to derive insights and discover optimization opportunities.',
        requirements: '4+ years in data analysis, proficient in SQL/Python, knowledge of statistics and machine learning'
      }
    }
  },
  contact: {
    left: {
      title: 'Let\'s Redesign Work Together',
      subtitle: 'We welcome enterprise solution and partnership inquiries.',
      email: 'Email',
      phone: 'Phone',
      offices: 'Offices'
    },
    seoul: {
      title: 'Seoul',
      address: '123 Teheran-ro\nGangnam-gu\nSeoul 06234, Korea'
    },
    singapore: {
      title: 'Singapore',
      address: '1 Marina Boulevard\n#20-01\nSingapore 018989'
    },
    form: {
      fullName: 'Full Name',
      company: 'Company',
      email: 'Email',
      phone: 'Phone',
      inquiryType: 'Inquiry Type',
      selectType: 'Select Type',
      types: {
        enterprise: 'Enterprise Solutions',
        partnership: 'Partnership',
        careers: 'Careers',
        media: 'Media',
        other: 'Other'
      },
      message: 'Message',
      submit: 'Send Inquiry',
      privacy: 'I agree to the privacy policy',
      success: 'Your inquiry has been sent successfully. We will contact you soon.'
    }
  },
  footer: {
    newsletter: {
      title: 'Stay Informed',
      description: 'Receive insights and updates about Hybrid Workforce.',
      placeholder: 'Email address',
      submit: 'Subscribe'
    },
    quickLinks: {
      title: 'Quick Links'
    },
    contact: {
      title: 'Contact',
      address: '10F, 509 Teheran-ro, Gangnam-gu, Seoul'
    },
    rights: 'All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service'
  }
};
